// JobsDrawerForm.tsx
import { asDate } from '@/common/application/utils/timeUtils';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';
import tid from '@/common/infrastructure/config/tid';
import DrawerFooter from '@/common/presentation/components/DrawerLayout/DrawerFooter';
import { FormikSubmitFormButton } from '@/common/presentation/components/forms/FormikSubmitFormButton/FormikSubmitFormButton';
import Button from '@/common/presentation/components/inputs/Button';
import { useFeatureFlag } from '@/common/presentation/hooks/useFeatureFlag';
import { JobFormData } from '@/jobs/application/types/JobTypes';
import { useJobForm } from '@/jobs/presentation/hooks/useJobForm';
import { JobValidationSchema } from '@/jobs/presentation/JobValidationSchema';
import AddIcon from '@mui/icons-material/Add';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Divider, Grid2 } from '@mui/material';
import { format } from 'date-fns';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOptionalFormikContext } from '@/common/presentation/hooks';
import { useJobTemporaryFormData } from '@/common/presentation/hooks/useJobTemporaryFormData';
import JobsDrawerFormFields from './JobsDrawerFormFields';

// Component to sync start date changes to parent
function StartDateLiveSync({ onChange }: { onChange?: (d: Date) => void }) {
  const formikContext = useOptionalFormikContext<JobFormData>();
  const values = formikContext?.values;
  const firstRunRef = useRef(true);
  const lastEmittedRef = useRef<string>('');

  useEffect(() => {
    if (firstRunRef.current) {
      firstRunRef.current = false;
      return;
    }
    if (!values?.startDate) return;
    const d = asDate(values.startDate);
    if (!d) return;

    const key = format(d, 'yyyy-MM-dd');
    if (key !== lastEmittedRef.current) {
      lastEmittedRef.current = key;
      onChange?.(d);
    }
  }, [values?.startDate, onChange]);

  return null;
}

export default function JobsDrawerForm({
  initialValues,
  isNew,
  formikStatus,
  onClose,
  selectedJobId,
  loading,
  jobId,
  isOpen,
  onStartDateChange,
  onAfterSave
}: Readonly<{
  initialValues: JobFormData;
  isNew: boolean;
  formikStatus: { loading: boolean };
  onClose: () => void;
  selectedJobId: number;
  loading: boolean;
  jobId: number;
  isOpen: boolean;
  onStartDateChange?: (nextDate: Date | string) => void;
  onAfterSave?: (nextDate: Date | string) => void;
}>) {
  const title = isNew ? 'Add Job' : 'Update Job';
  const isJobsDetailsEnabled = useFeatureFlag(FEATURE_FLAGS.JOBS_DETAILS_PAGE);
  const [isCreateCustomerModalOpen, setIsCreateCustomerModalOpen] = useState(false);
  const [createJobSiteModal, setCreateJobSiteModal] = useState(false);

  const { handleSubmit } = useJobForm(isNew, onClose);
  const navigate = useNavigate();
  const location = useLocation();
  const { setJobFormData, clearJobFormData } = useJobTemporaryFormData();

  // May be provided by a parent <Formik/> higher in the tree
  const parentFormik = useOptionalFormikContext<JobFormData>();

  const redirectToMoreDetails = (values: JobFormData) => {
    setJobFormData({ ...values });
    parentFormik?.setValues({ ...values });

    // Determine calendar context and compose next search params
    const searchParams = new URLSearchParams(location.search);
    const isFromCalendar = searchParams.get('submodule') === 'calendar';
    const nextSearch = new URLSearchParams(searchParams); // clone existing params

    if (isFromCalendar) {
      // Add calendar context to the URL params
      nextSearch.set('from', 'calendar');

      if (values.startDate) {
        const date = asDate(values.startDate);
        if (date) {
          nextSearch.set('calendarDate', format(date, 'yyyy-MM-dd'));
        }
      }

      if (values.startTime) {
        const time = asDate(values.startTime);
        if (time) {
          nextSearch.set('calendarTime', format(time, 'HH:mm'));
        }
      }
    }

    // Navigate with all params preserved
    const target = isNew ? './create' : `./${selectedJobId}`;
    const suffix = nextSearch.toString();
    navigate(suffix ? `${target}?${suffix}` : target);
  };

  const handleSubmitWithAfterSave = async (values: JobFormData, helpers: FormikHelpers<JobFormData>) => {
    await handleSubmit(values, helpers);
    const d = asDate(values.startDate);
    if (d) onAfterSave?.(d);

    if (isNew) {
      clearJobFormData();
    }
  };

  const handleClose = () => {
    if (isNew) {
      clearJobFormData();
    }
    onClose();
  };

  return (
    <Formik<JobFormData>
      enableReinitialize
      validateOnChange={false}
      validateOnBlur
      initialValues={initialValues || (parentFormik?.initialValues ?? initialValues)}
      validationSchema={JobValidationSchema}
      onSubmit={handleSubmitWithAfterSave}
      initialStatus={formikStatus}
    >
      {(formik: FormikProps<JobFormData>) => {
        const { isSubmitting, values } = formik;

        return (
          <Form noValidate {...tid('jobs:dispatch:drawer-form')}>
            <StartDateLiveSync onChange={onStartDateChange} />

            <JobsDrawerFormFields
              isNew={isNew}
              jobId={jobId}
              isOpen={isOpen}
              isCreateCustomerModalOpen={isCreateCustomerModalOpen}
              setIsCreateCustomerModalOpen={setIsCreateCustomerModalOpen}
              createJobSiteModal={createJobSiteModal}
              setCreateJobSiteModal={setCreateJobSiteModal}
            />

            <Divider />

            <DrawerFooter>
              <Grid2 container spacing={2} sx={{ paddingY: 2, paddingX: 3 }}>
                {/* Add More Details - Positioned at Top Right */}
                <Grid2 size={12} display="flex" justifyContent="flex-end">
                  <Button
                    disabled={isSubmitting || loading || !isJobsDetailsEnabled}
                    onClick={() => redirectToMoreDetails(values)}
                    startIcon={<AddCircleOutlineIcon />}
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                    {...tid('jobs-add-more-details-button')}
                  >
                    Add More Details
                  </Button>
                </Grid2>

                {/* Cancel and Save Buttons */}
                <Grid2 size={12} display="flex" justifyContent="flex-end" alignItems="center">
                  <Button
                    {...tid('jobs-cancel-drawer-button')}
                    variant="text"
                    onClick={handleClose}
                    sx={{ textTransform: 'none', fontWeight: 500, marginRight: 2 }}
                  >
                    Cancel
                  </Button>

                  <FormikSubmitFormButton
                    testId="jobs-save-button"
                    isHotkeyEnabled={isOpen && !(createJobSiteModal || isCreateCustomerModalOpen)}
                    variant="contained"
                    sx={{ paddingX: 3 }}
                    startIcon={<AddIcon />}
                    type="submit"
                    disabled={isSubmitting || loading}
                  >
                    {isSubmitting ? 'Saving...' : title}
                  </FormikSubmitFormButton>
                </Grid2>
              </Grid2>
            </DrawerFooter>
          </Form>
        );
      }}
    </Formik>
  );
}
